import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	DkvContext,
	ICartOrderInfo,
	IDkvContext,
	IOfferContext,
	ITollReservationInfo,
	ITypeVignettesOrderCompletedFields,
	IVignetteContext,
	OfferContext,
	VignetteContext,
	getVignettesOrderCompletedCollection,
} from '@adac-mp/shared';
import {
	Wrapper,
	PageLead,
	BestPriceIcon,
	OrderSummaryBox,
	Button,
	Typography,
	Richtext,
	ContentLoader,
} from '@adac-mp/components';
import { useQuery } from '@apollo/client';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { renderTeasers } from '../../utils/renderTeasers';

export const OrderConfirmationDkvPage = () => {
	const { lang, resetOffer } = useContext<IOfferContext>(OfferContext);
	const { contentfulLabels, updateContentfulLabels } = useContext<IVignetteContext>(VignetteContext);
	const { resetDkv } = useContext<IDkvContext>(DkvContext);
	const [orderInfo, setOrderInfo] = useState<ITollReservationInfo | ICartOrderInfo | undefined>(undefined);
	const unmountingComponent = useRef(false);
	const { viewport } = useViewport();

	const { error, data, loading } = useQuery(getVignettesOrderCompletedCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderCompletedCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderCompletedFields;
	const teaserItems = currentLabels?.teasers?.items || [];

	useEffect(() => {
		resetOffer();
		resetDkv();
		const currOrderInfo = JSON.parse(sessionStorage.getItem('orderInfo') || '{}');
		if (!currOrderInfo?.price) {
			window.location.replace('https://www.adac.de/fahrzeugwelt/maut-vignette');
		}
		setOrderInfo(currOrderInfo);
		sessionStorage.removeItem('customerHash');
		sessionStorage.removeItem('customerToken');
		sessionStorage.removeItem('orderDetail');
		sessionStorage.removeItem('offerHash');
		sessionStorage.removeItem('orderData');
		return () => {
			if (unmountingComponent.current) {
				sessionStorage.clear();
			} else {
				unmountingComponent.current = true;
			}
		};
	}, []);

	const getTotalOfferPrice = () => {
		return orderInfo?.price ? `${orderInfo.price.amount}€` : '';
	};

	const renderMautBoxOrderConfirmation = (mautBoxOrderInfo: ITollReservationInfo | ICartOrderInfo) => (
		<OrderSummaryBox
			variant="dkv"
			orderNumber={mautBoxOrderInfo?.orderNo || ''}
			totalPrice={getTotalOfferPrice()}
			hidePrice={false}
			additionalInfo={{
				title: 'Voraussichtliche Lieferung',
				label: 'in 7-10 Werktagen',
			}}
			itemLabel={'Bestellnummer'}
			priceLabel={'Gesamtpreis'}
		/>
	);

	const renderContactText = () => (
		<div className="flex items-center flex-col px-5 mb-5 md:mx-auto">
			<Typography component="p" size="sm" className="text-gray-300">
				{currentLabels?.contactText && <Richtext document={currentLabels.contactText as any} />}
			</Typography>
		</div>
	);

	const renderOrderConfirmation = () => (
		<section className="w-full text-center md:self-center md:w-6/12">
			<PageLead
				icon={<BestPriceIcon />}
				headline={currentLabels?.headline || 'Bereit für den nächsten Trip!'}
				description={
					currentLabels?.text ||
					'Sollten Sie keine Bestellbestätigung per E-Mail erhalten, wenden Sie sich bitte mit Ihrer Bestellnummer an: maut-service@adac.de'
				}
			/>
			{orderInfo && renderMautBoxOrderConfirmation(orderInfo)}
			{renderContactText()}
			<Button
				fullWidth
				data-cy="Button: home"
				href={currentLabels?.backLink || 'https://www.adac.de/fahrzeugwelt/maut-vignette'}
				className="md:max-w-[292px]">
				{currentLabels?.backLinkText || 'Zurück zu Maut & Vignetten'}
			</Button>
		</section>
	);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper
						className="bg-white"
						style={{
							paddingBottom: viewport === 'desktop' ? '64px' : '48px',
							paddingTop: viewport === 'desktop' ? '48px' : '24px',
						}}>
						{renderOrderConfirmation()}
					</Wrapper>
					{renderTeasers({ viewport, currentLabels, teaserItems })}
				</>
			)}
		</>
	);
};
